import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSSPMyInvoices, getSSPMyStats } from '../lib/api';
import { formatCurrency, formatDate, getBiltyStatusColor } from '../lib/utils';
import { Link } from 'react-router-dom';
import {
  Home, FileSpreadsheet, TrendingUp, Clock, CheckCircle,
  Search, ChevronLeft, ChevronRight, Eye, AlertCircle, Loader2,
  User, UserCheck, Shield
} from 'lucide-react';

export default function MyPortalPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [biltyFilter, setBiltyFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, statsRes] = await Promise.all([
        getSSPMyInvoices({ page, limit: 20, search, bilty_status: biltyFilter }),
        getSSPMyStats(),
      ]);
      setInvoices(invRes.data.invoices || []);
      setTotal(invRes.data.total || 0);
      setPages(invRes.data.pages || 1);
      if (invRes.data.message) setMessage(invRes.data.message);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [page, search, biltyFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const roleLabel = {
    'BOSS_ADMIN': 'Administrator',
    'SCL_AGENT': 'Supply Chain Agent',
    'SCRM_PARTY': 'Party User',
    'BIA_VIEWER': 'Analytics Viewer',
  };

  const RoleIcon = {
    'BOSS_ADMIN': Shield,
    'SCL_AGENT': UserCheck,
    'SCRM_PARTY': User,
    'BIA_VIEWER': TrendingUp,
  };

  const Icon = RoleIcon[user?.role] || User;

  return (
    <div className="space-y-6" data-testid="my-portal-page">
      {/* Welcome Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-boss-violet/10 flex items-center justify-center">
            <Icon size={28} className="text-boss-violet" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-boss-text" data-testid="portal-welcome">
              Welcome, {user?.name || 'User'}
            </h1>
            <p className="text-sm text-boss-text-muted">
              {roleLabel[user?.role] || user?.role} &mdash; Self-Service Portal
            </p>
          </div>
        </div>

        {message && (
          <div className="mt-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-center gap-2">
            <AlertCircle size={16} /> {message}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-boss-border" data-testid="stat-total-invoices">
            <div className="inline-flex p-2 rounded-lg bg-blue-50 text-blue-600 mb-2"><FileSpreadsheet size={16} /></div>
            <div className="text-xl font-bold text-boss-text">{stats.total_invoices}</div>
            <div className="text-xs text-boss-text-muted">My Invoices</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-boss-border" data-testid="stat-total-sales">
            <div className="inline-flex p-2 rounded-lg bg-green-50 text-green-600 mb-2"><TrendingUp size={16} /></div>
            <div className="text-xl font-bold text-boss-text">{formatCurrency(stats.total_sales)}</div>
            <div className="text-xs text-boss-text-muted">Total Sales</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-boss-border" data-testid="stat-pending">
            <div className="inline-flex p-2 rounded-lg bg-yellow-50 text-yellow-600 mb-2"><Clock size={16} /></div>
            <div className="text-xl font-bold text-boss-text">{stats.pending_bilties}</div>
            <div className="text-xs text-boss-text-muted">Pending Bilties</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-boss-border" data-testid="stat-uploaded">
            <div className="inline-flex p-2 rounded-lg bg-green-50 text-green-600 mb-2"><CheckCircle size={16} /></div>
            <div className="text-xl font-bold text-boss-text">{stats.uploaded_bilties}</div>
            <div className="text-xs text-boss-text-muted">Uploaded Bilties</div>
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-boss-border">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted" />
            <input
              type="text" placeholder="Search bill no, party..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
              data-testid="portal-search"
            />
          </div>
          <select
            value={biltyFilter} onChange={e => { setBiltyFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-boss-border text-sm"
            data-testid="portal-bilty-filter"
          >
            <option value="">All Bilty Status</option>
            <option value="pending">Pending</option>
            <option value="uploaded">Uploaded</option>
            <option value="verified">Verified</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="portal-invoices-table">
            <thead>
              <tr className="border-b border-boss-border bg-boss-surface-2">
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Bill No.</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Date</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Party</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Transport</th>
                <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Station</th>
                <th className="text-right py-3 px-4 font-medium text-boss-text-muted">Amount</th>
                <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Bilty</th>
                <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-boss-border/50">
                    {[...Array(8)].map((_, j) => <td key={j} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td>)}
                  </tr>
                ))
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-boss-text-muted">
                  <FileSpreadsheet size={32} className="mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No invoices found for your profile</p>
                  <p className="text-xs mt-1">Contact your administrator to link your profile</p>
                </td></tr>
              ) : (
                invoices.map(inv => (
                  <tr key={inv._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50 transition-colors" data-testid={`portal-row-${inv._id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs font-medium">{inv.bill_no_full}</td>
                    <td className="py-2.5 px-4 text-boss-text-muted">{formatDate(inv.bill_date)}</td>
                    <td className="py-2.5 px-4 max-w-[150px] truncate">{inv.party_name}</td>
                    <td className="py-2.5 px-4">{inv.transport}</td>
                    <td className="py-2.5 px-4">{inv.station}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{formatCurrency(inv.amount_grand_total)}</td>
                    <td className="py-2.5 px-4 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getBiltyStatusColor(inv.bilty_status)}`}>
                        {inv.bilty_status || 'pending'}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      <Link to={`/boss/fa/invoices/${inv._id}`} className="inline-flex items-center gap-1 text-boss-violet hover:underline text-xs">
                        <Eye size={14} /> View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-boss-border">
            <span className="text-sm text-boss-text-muted">Page {page} of {pages} ({total} total)</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-2 rounded-lg border border-boss-border hover:bg-boss-surface-2 disabled:opacity-30">
                <ChevronLeft size={16} />
              </button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-2 rounded-lg border border-boss-border hover:bg-boss-surface-2 disabled:opacity-30">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
